import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaLightbulb,
  FaLock,
  FaStar,
  FaShare, // Add this import for the share icon
} from "react-icons/fa";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Text from "../components/text";
import ShareModal from "../components/shareModal"; // Import ShareModal component
import { shareFavoriteList } from "../api/getApi"; // Change this import

// Helper to classify college
const getSafetyCategory = (userRank, closeRank) => {
  if (!userRank || !closeRank || isNaN(userRank) || isNaN(closeRank))
    return "N/A";
  const lower = userRank * 0.95;
  const upper = userRank * 1.05;
  if (closeRank >= upper) return "safe";
  if (closeRank <= lower) return "ambitious";
  return "moderate";
};

// Helper to get color class
const getColor = (category) => {
  if (category === "safe") return "bg-green-100 border-l-4 border-green-500";
  if (category === "moderate")
    return "bg-yellow-100 border-l-4 border-yellow-500";
  if (category === "ambitious") return "bg-red-100 border-l-4 border-red-500";
  return "bg-gray-100 border-l-4 border-gray-500";
};

// Helper to get icon
const getIcon = (category) => {
  if (category === "safe") return <FaCheckCircle className="text-green-500" />;
  if (category === "moderate")
    return <FaCheckCircle className="text-yellow-500" />;
  if (category === "ambitious")
    return <FaExclamationTriangle className="text-red-500" />;
  return <FaExclamationTriangle className="text-gray-500" />;
};

// Helper to get rank key
const getRankKey = (formData) => {
  if (!formData.caste || !formData.gender) return "";
  return `${formData.caste}_${formData.gender === "Male" ? "boys" : "girls"}`;
};

const ReviewReportWithPdf = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedColleges = [], formData = {}, favoriteIndex } = location.state || {}; // Ensure favoriteIndex is received
  const userRank = Number(formData.rank);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add state for share functionality
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [isSharing, setIsSharing] = useState(false);

  // Calculate counts and categories
  let safeCount = 0,
    moderateCount = 0,
    ambitiousCount = 0;
  const collegeCategories = selectedColleges.map((college) => {
    const closeRank = Number(college[getRankKey(formData)]);
    const category = getSafetyCategory(userRank, closeRank);
    if (category === "safe") safeCount++;
    else if (category === "moderate") moderateCount++;
    else if (category === "ambitious") ambitiousCount++;
    return { ...college, closeRank, category };
  });

  // Recommendations flags
  const totalColleges = selectedColleges.length;
  const needsMoreSafe = userRank < 5000 ? false : safeCount < 4;
  const needsMoreModerate = userRank < 5000 ? false : moderateCount < 4;
  const needsMoreAmbitious = userRank < 5000 ? false : ambitiousCount < 4;
  const needsMoreTotal =
    userRank >= 20000
      ? totalColleges < 30
      : userRank < 20000 && userRank >= 5000
      ? totalColleges < 12
      : false;

  // PDF logic
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Add handleShare function
  const handleShare = async () => {
    // Ensure the list has been saved and has an index before sharing
    if (favoriteIndex === undefined || favoriteIndex === null) {
      alert("Please save this list first before sharing.");
      return;
    }

    setIsSharing(true);
    try {
      // Use the favoriteIndex to get a unique share ID from the backend
      const shareId = await shareFavoriteList(favoriteIndex); 
      const shareableLink = `${window.location.origin}/shared/${shareId}`;
      setShareLink(shareableLink);
      setShareModalOpen(true);
    } catch (error) {
      console.error("Error creating share link:", error);
      alert(error.message || "Failed to create sharing link. Please try again.");
    } finally {
      setIsSharing(false);
    }
  };

  const handleGeneratePdf = () => {
    setIsGeneratingPdf(true);
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "px",
      format: "a4",
    });

    pdf.setFontSize(16);
    pdf.text(
      Text.PrintPdf?.CollegeSelectionReport || "College Selection Report- collegerank.in",
      pdf.internal.pageSize.getWidth() / 2,
      40,
      { align: "center" }
    );

    pdf.setFontSize(12);
    pdf.text(Text.PrintPdf?.StudentInfo || "Student Info", 40, 60);
    pdf.setFontSize(10);
    pdf.text(
      `${Text.PrintPdf?.Gender || "Gender"}: ${
        formData.gender || "Not provided"
      }`,
      40,
      80
    );
    pdf.text(
      `${Text.PrintPdf?.Rank || "Rank"}: ${formData.rank || "Not provided"}`,
      40,
      90
    );
    pdf.text(
      `${Text.PrintPdf?.Category || "Category"}: ${
        formData.caste?.toUpperCase() || "Not provided"
      }`,
      40,
      100
    );

    if (selectedColleges.length > 0) {
      const rankKey = getRankKey(formData);
      autoTable(pdf, {
        startY: 120,
        head: [
          ["#", "Code", "College Name", "Location", "Branch", "Close Rank"],
        ],
        body: selectedColleges.map((college, index) => [
          index + 1,
          college.inst_code,
          college.inst_name,
          college.place,
          college.branch_code,
          college[rankKey] || "N/A",
        ]),
        theme: "grid",
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 30 },
          2: { cellWidth: "auto" },
          3: { cellWidth: "auto" },
          4: { cellWidth: 30 },
          5: { cellWidth: 40 },
        },
      });
    } else {
      pdf.setFontSize(10);
      pdf.text("No colleges selected.", 40, 120);
    }

    pdf.setFontSize(8);
    pdf.text(
      `Generated on: ${new Date().toLocaleString()}`,
      pdf.internal.pageSize.getWidth() / 2,
      pdf.internal.pageSize.getHeight() - 10,
      { align: "center" }
    );

    pdf.save("College_Selection_Report.pdf");
    setIsGeneratingPdf(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-indigo-50 py-4 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 bg-white rounded-lg p-4 shadow-sm border border-indigo-100">
          <h1 className="text-xl font-bold text-indigo-800 mb-2">
            Congratulations! 🎉
          </h1>
          <p className="text-base text-gray-700 mb-2">
            Rank{" "}
            <span className="font-bold text-indigo-600">
              {userRank?.toLocaleString?.() || "-"}
            </span>{" "}
            in EAPCET
          </p>
          <div className="inline-block bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full font-semibold text-sm">
            {totalColleges} College{totalColleges !== 1 ? "s" : ""} Selected
          </div>
        </div>

        {/* College Selection Report */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6 overflow-x-auto">
          
          <h2 className="text-base font-bold mb-3 text-gray-800 flex items-center flex-wrap">
            <FaLock className="mr-1 text-indigo-600" />
            <span className="mr-auto">Your College Selection</span>
            <button
              type="button"
              onClick={handleGeneratePdf}
              disabled={selectedColleges.length === 0 || isGeneratingPdf}
              className={`ml-3 mt-2 sm:mt-0 px-2 py-1 text-xs flex items-center rounded-md ${
                selectedColleges.length === 0 || isGeneratingPdf
                  ? "bg-blue-200 text-blue-400 cursor-not-allowed"
                  : "bg-blue-100 hover:bg-blue-200 text-blue-700"
              }`}
              title="Download PDF"
            >
              {isGeneratingPdf ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 mr-1"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  <span>Download</span>
                </>
              )}
            </button>
            
            {/* Share Button */}
            <button
              type="button"
              onClick={handleShare}
              disabled={isSharing || favoriteIndex === undefined}
              className={`ml-2 mt-2 sm:mt-0 px-2 py-1 text-xs flex items-center rounded-md ${
                isSharing || favoriteIndex === undefined
                  ? "bg-blue-200 text-blue-400 cursor-not-allowed"
                  : "bg-blue-100 hover:bg-blue-200 text-blue-700"
              }`}
              title={favoriteIndex !== undefined ? "Share this list" : "Save list first to share"}
            >
              {isSharing ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 mr-1"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Sharing...</span>
                </>
              ) : (
                <>
                  <FaShare className="h-4 w-4 mr-1" />
                  <span>Share</span>
                </>
              )}
            </button>
          </h2>
          
          <table className="w-full text-sm divide-y divide-gray-200">
            <thead className="bg-indigo-50">
              <tr>
                <th className="px-2 py-1 text-left text-xs font-semibold text-indigo-700 uppercase">
                  #
                </th>
                <th className="px-2 py-1 text-left text-xs font-semibold text-indigo-700 uppercase">
                  College
                </th>
                <th className="px-2 py-1 text-left text-xs font-semibold text-indigo-700 uppercase">
                  Branch
                </th>
                <th className="px-2 py-1 text-left text-xs font-semibold text-indigo-700 uppercase">
                  Close Rank
                </th>
                <th className="px-2 py-1 text-left text-xs font-semibold text-indigo-700 uppercase">
                  Chance Level
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {collegeCategories.map((college, idx) => (
                <tr
                  key={idx}
                  className={`${getColor(
                    college.category
                  )} hover:bg-indigo-50 transition-colors`}
                >
                  <td className="px-2 py-1 font-medium text-gray-900">
                    {idx + 1}
                  </td>
                  <td className="px-2 py-1">
                    <div className="font-semibold text-gray-900">
                      {college.inst_name}
                    </div>
                    <div className="text-xs text-gray-500">
                      Code: {college.inst_code}
                    </div>
                  </td>
                  <td className="px-2 py-1 font-medium text-gray-900">
                    {college.branch_code}
                  </td>
                  <td className="px-2 py-1 font-semibold">
                    {college.closeRank || "N/A"}
                  </td>
                  <td className="px-2 py-1 flex items-center">
                    {/* {getIcon(college.category)} */}
                    <span className="ml-1 capitalize font-semibold">
                      {college.category}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Selection Balance Meter */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-indigo-100">
          <h3 className="text-base font-bold mb-2 text-gray-800">
            Selection Balance
          </h3>
          <div className="flex flex-wrap justify-between text-sm">
            <p className="text-red-600 font-semibold">
              {ambitiousCount} Top-Choice
            </p>
            <p className="text-yellow-600 font-semibold">
              {moderateCount} In-Hand
            </p>
            <p className="text-green-600 font-semibold">{safeCount} Back-Up</p>
          </div>
          <hr className="mt-2 border-gray-300" />
          <div className="hidden md:block">
            <p className="text-sm mt-2 text-red-700">
              Competitive colleges (Closing rank above your rank)
            </p>
            <p className="text-sm mt-2 text-yellow-700">
              Good chance colleges (Closing rank near your rank)
            </p>
            <p className="text-sm mt-2 text-green-700">
              High chance colleges (Closing rank much below your rank)
            </p>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-indigo-100">
          <h3 className="text-base font-bold mb-3 text-indigo-800 flex items-center">
            <FaExclamationTriangle className="mr-1 text-yellow-500" />
            Recommendations
          </h3>
          <div className="space-y-2 text-sm">
            {userRank < 5000 && totalColleges < 10 && (
              <div className="p-2 bg-blue-50 rounded border-l-2 border-blue-500">
                <h4 className="font-semibold text-blue-800">
                  Add More Colleges
                </h4>
                <p className="text-blue-700">
                  With a rank of {userRank.toLocaleString()}, consider selecting
                  at least 10 colleges to maximize your options.
                </p>
              </div>
            )}
            {needsMoreTotal && (
              <div className="p-2 bg-blue-50 rounded border-l-2 border-blue-500">
                <h4 className="font-semibold text-blue-800">
                  Increase Total Selections
                </h4>
                <p className="text-blue-700">
                  You have selected {totalColleges} colleges.{" "}
                  {userRank >= 20000
                    ? "Select at least 30 colleges for better chances with your rank."
                    : "Select at least 12 colleges (4 in each category) to meet the minimum requirement."}
                </p>
              </div>
            )}
            {needsMoreAmbitious && (
              <div className="p-2 bg-red-50 rounded border-l-2 border-red-500">
                <h4 className="font-semibold text-red-800">Dream Bigger!</h4>
                <p className="text-red-700">
                  Only {ambitiousCount} ambitious colleges. Add at least{" "}
                  {4 - ambitiousCount} more dream colleges with closing ranks
                  slightly below your rank.
                </p>
              </div>
            )}
            {needsMoreModerate && (
              <div className="p-2 bg-yellow-50 rounded border-l-2 border-yellow-500">
                <h4 className="font-semibold text-yellow-800">
                  More Moderate Options
                </h4>
                <p className="text-yellow-700">
                  You have {moderateCount} moderate choices. Add at least{" "}
                  {4 - moderateCount} more colleges with closing ranks close to
                  your rank.
                </p>
              </div>
            )}
            {needsMoreSafe && (
              <div className="p-2 bg-blue-50 rounded border-l-2 border-blue-500">
                <h4 className="font-semibold text-blue-800">
                  Add Safe Colleges
                </h4>
                <p className="text-blue-700">
                  Only {safeCount} safe colleges. Add at least {4 - safeCount}{" "}
                  more colleges where your rank is much better than the closing
                  rank.
                </p>
              </div>
            )}
            {!needsMoreTotal &&
              !needsMoreSafe &&
              !needsMoreModerate &&
              !needsMoreAmbitious && (
                <div className="p-2 bg-green-50 rounded border-l-2 border-green-500">
                  <h4 className="font-semibold text-green-800">
                    Perfect Balance! 🎯
                  </h4>
                  <p className="text-green-700">
                    Your selection meets all requirements with a great mix of
                    colleges.
                  </p>
                </div>
              )}
          </div>
        </div>

        {/* Pro Tips */}
        <div className="bg-gradient-to-r from-indigo-700 to-purple-800 rounded-lg shadow-sm p-4 text-white mb-6">
          <h3 className="text-base font-bold mb-2 flex items-center">
            <FaLightbulb className="mr-1 text-yellow-300" />
            Pro Tips
          </h3>
          <div className="space-y-2 text-sm">
            {/* Existing Safety Margin tip */}
            <div>
              <h4 className="font-semibold text-yellow-300">
                Safety Margin Strategy
              </h4>
              <p>
                Use 30% safety margin when filtering colleges - include colleges with closing ranks up to 30% above your rank for better chances.
              </p>
            </div>

            {/* Add new Complete List tip */}
            <div>
              <h4 className="font-semibold text-yellow-300">
                View Complete College List
              </h4>
              <p>
              Use rank 1 to view all available colleges and branches.</p>
            </div>

            {/* Rest of existing tips */}
            <div>
              <h4 className="font-semibold text-yellow-300">
                Choice Order Strategy
              </h4>
              <p>
                Order: Dream colleges → Moderate → Safe colleges to prioritize
                preferences.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-yellow-300">
                Seat Protection Rule
              </h4>
              <p>
                Use "Float" instead of "Freeze" in early rounds to upgrade
                seats.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-yellow-300">
                Maximize Choices
              </h4>
              <p>Fill 15+ option slots to increase security.</p>
            </div>
            <div>
              <h4 className="font-semibold text-yellow-300">Deadline Alert</h4>
              <p>
                Set reminders for web options locking, fee payment, and
                reporting deadlines.
              </p>
            </div>
            <div className="p-2 bg-yellow-900 bg-opacity-40 rounded">
              <h4 className="font-semibold text-yellow-300 flex items-center">
                <FaStar className="mr-1 text-yellow-300" />
                Golden Rule
              </h4>
              <p>
                Better branch in a good college &gt; Best college with an
                average branch
              </p>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center mt-6">
          <button
            onClick={() => {
              console.log("Navigating to update page with favoriteIndex:", favoriteIndex);
              navigate("/update", {
                state: {
                  favoriteId: favoriteIndex // Keep as favoriteId for update page compatibility
                }
              });
            }}
            className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-700 text-white rounded-full hover:scale-105 transition-all duration-200 font-semibold text-sm"
          >
            Edit Selection
          </button>
          <p className="mt-2 text-sm text-gray-600">
            Modify choices until the web options locking date.
          </p>
        </div>

        {/* PDF Download Section */}
        <div className="mt-10 flex flex-col items-center">
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={handleGeneratePdf}
              disabled={selectedColleges.length === 0 || isGeneratingPdf}
              className={`px-6 py-2.5 flex items-center justify-center rounded-lg shadow-sm ${
                selectedColleges.length === 0 || isGeneratingPdf
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 transition-colors"
              } text-white font-semibold`}
            >
              {isGeneratingPdf ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>{Text.PrintPdf?.GeneraingPdf || "Generating PDF..."}</span>
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  <span>{Text.PrintPdf?.DownloadPdf || "Download PDF"}</span>
                </>
              )}
            </button>
            
            {/* Add Share Button */}
            <button
              type="button"
              onClick={handleShare}
              disabled={isSharing || favoriteIndex === undefined}
              className={`px-6 py-2.5 flex items-center justify-center rounded-lg shadow-sm ${
                isSharing || favoriteIndex === undefined
                  ? "bg-indigo-400 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700 transition-colors"
              } text-white font-semibold`}
              title={favoriteIndex !== undefined ? "Share this list" : "Save list first to share"}
            >
              {isSharing ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Sharing...</span>
                </>
              ) : (
                <>
                  <FaShare className="h-4 w-4 mr-2" />
                  <span>Share List</span>
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {favoriteIndex !== undefined
              ? "Download or share your selection for offline use." 
              : "Save your list first to enable sharing."}
          </p>
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        link={shareLink}
      />
    </div>
  );
};

export default ReviewReportWithPdf;
